import { gql } from '@apollo/client'
import { BANK_ACCOUNT_FRAGMENT } from './bankAccounts'
import { KYC_STATUS_BRIEF_FRAGMENT } from './verification'

const METHOD_FI_ENTITY_FRAGMENT = gql`
    fragment FullMethodFiEntity on MethodFiEntity {
        id
        canReceive
        canRetrieveData
        authenticated
        locked
        active
        error
        status
        address {
            zip
        }
    }
`

gql`
    query CanAddBills {
        me {
            id
            kycStatus {
                ...KycStatusBrief
            }
            methodFiEntity {
                ...FullMethodFiEntity
            }
        }
    }
    ${KYC_STATUS_BRIEF_FRAGMENT}
`

export const AUTH_TOKEN_QUERY = gql`
    query MethodFiAuthToken {
        getMethodFiAuthToken
    }
`

gql`
    query BillConnectionData {
        methodFiEntity {
            ...FullMethodFiEntity
        }
    }
    ${METHOD_FI_ENTITY_FRAGMENT}
`

gql`
    fragment MerchantSummary on MethodFiMerchant {
        id
        name
        parentName
        logo
        description
        types
    }
`

gql`
    mutation AddCustomBill($merchantId: String!, $accountId: String!, $type: BillType) {
        addAccount(merchantId: $merchantId, accountId: $accountId, type: $type) {
            id
            nickname
            manual
            capabilities
        }
    }
`

gql`
    query Merchants($name: String, $type: BillType) {
        merchants(name: $name, type: $type) {
            ...MerchantSummary
        }
    }
`

gql`
    query PopularMerchants($type: BillType) {
        popularMerchants(type: $type) {
            ...MerchantSummary
        }
    }
`

export const BILL_INSTITUTION_FRAGMENT = gql`
    fragment Institution on BillInstitution {
        id
        institutionId
        logo
        logoUrl
        name
    }
`

export const FULL_BILL_FRAGMENT = gql`
    fragment FullBill on Bill {
        id
        accountId
        provider
        connected
        verified
        mask
        type
        name
        originalName
        manual
        canReceive
        balance
        openedAt
        nextPaymentDueDate
        amountDue
        syncStatus
        lastSync
        createdAt
        testPaymentAvailable
        institution {
            ...Institution
        }
    }
    ${BILL_INSTITUTION_FRAGMENT}
`

export const SUBSCRIPTION = gql`
    fragment FullPaymentSubscription on PaymentSubscription {
        id
        accountId
        provider
        type
        amount
        network
        startDate
        nextSubscriptionDate
        frequency
        frequencyValue
        blockchainSubscriptionId
        walletAddress
        paymentToken
        tokenSymbol
        subscriptionCount
        totalPayments
        processing
        error {
            type
            retryCount
            nextRetry
        }
    }
`

export const FULL_POPULAR_BILL_FRAGMENT = gql`
    fragment FullPopularBill on PopularBill {
        id
        name
        count
        type
        institution {
            ...Institution
        }
    }
    ${BILL_INSTITUTION_FRAGMENT}
`

export const POPULAR_BILLS_QUERY = gql`
    query PopularBills {
        popularBills {
            ...FullPopularBill
        }
    }
    ${FULL_POPULAR_BILL_FRAGMENT}
`

export const BILLS_QUERY = gql`
    query BillsData {
        bills {
            ...FullBill
        }
        subscriptions {
            ...FullPaymentSubscription
        }
        bankAccounts(ownedByUser: false) {
            ...FullBankAccount
        }
    }
    ${FULL_BILL_FRAGMENT}
    ${SUBSCRIPTION}
    ${BANK_ACCOUNT_FRAGMENT}
`

gql`
    mutation ConnectBills($accountIds: [String!]!) {
        connectBills(accountIds: $accountIds)
    }
`
gql`
    mutation DeleteBill($accountId: String!, $provider: AccountProvider) {
        deleteBill(accountId: $accountId, provider: $provider)
    }
`

gql`
    mutation RenameBill($accountId: String!, $provider: AccountProvider, $name: String!) {
        renameBill(accountId: $accountId, provider: $provider, name: $name) {
            ...FullBill
        }
    }
`
gql`
    mutation UpdateBillType($accountId: String!, $provider: AccountProvider, $type: BillType!) {
        updateBillType(accountId: $accountId, provider: $provider, type: $type) {
            ...FullBill
        }
    }
`

gql`
    mutation LockEntity {
        lockEntityAuth {
            ...FullMethodFiEntity
        }
    }
`

gql`
    mutation UpdateEntityAuthentication($connectionId: String) {
        updateEntityAuthentication(connectionId: $connectionId) {
            ...FullMethodFiEntity
        }
    }
`

gql`
    mutation CreatePaymentSubscription(
        $createSubscriptionInput: CreateSubscriptionInput!
        $signatureParams: SignatureParamsInput
    ) {
        createSubscription(
            createSubscriptionInput: $createSubscriptionInput
            signatureParams: $signatureParams
        ) {
            ...FullPaymentSubscription
        }
    }
    ${SUBSCRIPTION}
`
